import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalDateKey } from './dateUtils';
import { getFlexiblePrecision, getFlexibleRange, getTaskColorClass } from './scheduleUtils';
import { TimelineYearTasksModal } from './TimelineYearTasksModal';
import { sortPlannerTasks } from './taskListOrder';
import { PlannerTaskInfo } from './types';
import { useTimelineView } from './TimelineViewContext';

const getMonths = (language: string) => Array.from({ length: 12 }, (_, month) => (
    new Date(2024, month, 1).toLocaleDateString(language, { month: 'short' })
));

const getYearRangeClass = (task: PlannerTaskInfo['task'], year: number, month: number) => {
    const { start, end } = getFlexibleRange(task);
    if (start.getFullYear() === year && month === 0) return 'range-start';
    if (end.getFullYear() === year && month === 11) return 'range-end';
    return 'range-middle';
};

const TimelineYearsYear: React.FC<{ date: Date; monthLabels: string[]; onOpen: () => void }> = ({
    date, monthLabels, onOpen,
}) => {
    const { getTasksForMonth, getTasksForYear, onScaleChange } = useTimelineView();
    const year = date.getFullYear();
    const tasks = sortPlannerTasks(getTasksForYear(year));
    const yearTasks = tasks.filter(({ task }) => getFlexiblePrecision(task) === 'year').slice(0, 2);
    return <div className="timeline-years-year">
        <span className="timeline-years-year-heading">
            <button type="button" className="timeline-years-year-title" onClick={() => onScaleChange('year', date)}>{year}</button>
            <span className={`timeline-years-year-count ${tasks.length ? '' : 'is-empty'}`}>{tasks.length}</span>
        </span>
        <button type="button" className="timeline-years-months" onClick={onOpen} aria-label={year.toString()}>
            {monthLabels.map((label, month) => {
                const monthDate = new Date(year, month, 1);
                const dots = sortPlannerTasks(getTasksForMonth(monthDate)
                    .filter(({ task }) => getFlexiblePrecision(task) !== 'year')).slice(0, 3);
                return <span key={label} className="timeline-years-month">
                    <span>{label}</span>
                    {yearTasks.length > 0 && <span className="timeline-years-month-ranges">
                        {yearTasks.map(({ task }) => <span key={task.id} data-timeline-task-id={task.id}
                            className={`timeline-years-range ${getYearRangeClass(task, year, month)} ${task.completed ? 'completed' : ''}`} />)}
                    </span>}
                    {dots.length > 0 && <span className="timeline-years-month-dots">
                        {dots.map(({ task }) => <span key={task.id} data-timeline-task-id={task.id}
                            className={`${getTaskColorClass(task)} ${task.completed ? 'completed' : ''}`} title={task.title} />)}
                    </span>}
                </span>;
            })}
        </button>
    </div>;
};

export const TimelineYearsCalendar: React.FC<{ current: boolean; date: Date }> = ({ current, date }) => {
    const { i18n } = useTranslation();
    const { onRequestTaskListReturn, onTaskListOpened, taskListToOpen } = useTimelineView();
    const [openYear, setOpenYear] = useState<Date | null>(null);
    const monthLabels = useMemo(() => getMonths(i18n.language), [i18n.language]);
    const startYear = date.getFullYear() - 5;
    React.useEffect(() => {
        const year = taskListToOpen?.date.getFullYear();
        if (taskListToOpen?.scope !== 'year' || year === undefined || year < startYear || year > startYear + 11) return;
        setOpenYear(taskListToOpen.date);
        onTaskListOpened();
    }, [onTaskListOpened, startYear, taskListToOpen]);
    return <section className="timeline-period timeline-period-years timeline-years-calendar"
        data-timeline-current-period={current || undefined} data-timeline-label={`${startYear} - ${startYear + 11}`}
        data-timeline-period-key={`years-${startYear}`} data-timeline-date={getLocalDateKey(date)}
        data-timeline-years-date={new Date(date.getFullYear(), 0, 1).toISOString()}>
        <header className="timeline-years-calendar-heading"><h2>{startYear} – {startYear + 11}</h2></header>
        <div className="timeline-years-calendar-grid">{Array.from({ length: 12 }, (_, index) => {
            const yearDate = new Date(startYear + index, 0, 1);
            return <TimelineYearsYear key={yearDate.getFullYear()} date={yearDate} monthLabels={monthLabels}
                onOpen={() => setOpenYear(yearDate)} />;
        })}</div>
        {openYear && <TimelineYearTasksModal date={openYear} onClose={() => setOpenYear(null)}
            onPrepareAction={() => {
                onRequestTaskListReturn('year', openYear);
                setOpenYear(null);
            }} />}
    </section>;
};
